// react
import { ReactNode, SyntheticEvent, useContext, useState } from 'react';

// types
import {
	Act,
	Comment,
	Contract,
	Document,
	Entity,
	Frame,
	Pagination,
	Transaction,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import {
	Box,
	Grid,
	LinearProgress,
	LinearProgressProps,
	Paper,
	Tooltip,
	Typography,
} from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';

// theme
import { theme } from '../../../theme';

// icons
import {
	AddCommentOutlined as IconAddComment,
	CheckOutlined as IconCheck,
	CloseOutlined as IconClose,
	InsertDriveFileOutlined as IconInsertDriveFile,
	ModeEditOutlined as IconModeEdit,
	SendOutlined as IconSend,
} from '@mui/icons-material';

// context
import { ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// dialogs
import { DialogAddComment } from '../../dialogs/DialogAddComment/DialogAddComment';
import { DialogEditContract } from '../../dialogs/DialogEditContract/DialogEditContract';
import {
	DialogUpdateContractStatus,
	DialogUpdateContractStatusProps as DialogUpdateContractStatusPropsType,
} from '../../dialogs/DialogUpdateContractStatus/DialogUpdateContractStatus';
import { DialogUploadDocs } from '../../dialogs/DialogUploadDocs/DialogUploadDocs';

// ui
import { Amount } from '../../ui/Amount/Amount';
import { BoxDescription } from '../../ui/BoxDescription/BoxDescription';
import { BtnExpand } from '../../ui/BtnExpand/BtnExpand';
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../ui/ChipStatus/ChipStatus';
import { UserAvatarModified } from '../../ui/UserAvatarModified/UserAvatarModified';
import { IconCircle } from '../../ui/IconCircle/IconCircle';
import { MenuItemProps } from '../../ui/Menu/Menu';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { Tabs } from '../../ui/Tabs/Tabs';
import { Time } from '../../ui/Time/Time';
import { TypographyCaption } from '../../ui/TypographyCaption/TypographyCaption';

// links
import { LinkEntity } from '../../links/LinkEntity/LinkEntity';

// tables
import { TableBasicActs } from '../../tables/basic/TableBasicActs/TableBasicActs';
import { TableBasicComments } from '../../tables/basic/TableBasicComments/TableBasicComments';
import { TableBasicDocs } from '../../tables/basic/TableBasicDocs/TableBasicDocs';
import { TableBasicEntities } from '../../tables/basic/TableBasicEntities/TableBasicEntities';
import { TableBasicTransactions } from '../../tables/basic/TableBasicTransactions/TableBasicTransactions';

// config
import { featureFlags } from '../../../config/featureFlags';

// utils
import { defaultPagination } from '../../../utils/defaults';

export interface RenderKeyValueParams {
	label: string;
	value: ReactNode;
}

export interface CardContractProps {
	contract: Contract;
	frame?: Frame;
	hasDialogEditContract?: boolean;
	refetchContracts?: () => void;
}

export const CardContract = ({
	contract,
	frame = 'SUPPLIERS',
	hasDialogEditContract = false,
	refetchContracts,
}: CardContractProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// state
	const [acts, setActs] = useState<Act[]>([]);
	const [comments, setComments] = useState<Comment[]>([]);
	const [documents, setDocuments] = useState<Document[]>([]);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [isExpanded, setIsExpanded] = useState(false);
	const [paginationDocuments, setPaginationDocuments] = useState<Pagination>(defaultPagination);
	const [tabValue, setTabValue] = useState('transactions');
	const [transactions, setTransactions] = useState<Transaction[]>([]);

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogContract = searchParams.get('dialogContract');

	// fetch transactions
	const urlFetchTransactions = `${process.env.REACT_APP_API_URL}/transactions?contract=${contract.id}&populate=activities,activities.actor,expenseType&orderBy=date&order=DESC`;

	const { isLoading: isLoadingTransactions } = useFetch({
		isEnabled: Boolean(contract && isExpanded && tabValue === 'transactions'),
		url: urlFetchTransactions,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setTransactions(res.data);
			}
		},
	});

	// fetch entities
	const urlFetchEntities = `${process.env.REACT_APP_API_URL}/entities?contract=${contract.id}&type=SUPPLIER`;

	const { isLoading: isLoadingEntities } = useFetch({
		isEnabled: Boolean(contract && isExpanded && tabValue === 'suppliers'),
		url: urlFetchEntities,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}
		},
	});

	// fetch documents
	const urlFetchDocs = `${process.env.REACT_APP_API_URL}/documents?contract=${contract.id}&limit=10&page=1&fields=created,notarizationHash,id,name,type,user&populate=type,user`;

	const { isLoading: isLoadingDocs, refetch: refetchDocuments } = useFetch({
		isEnabled: Boolean(contract && isExpanded && tabValue === 'documents'),
		url: urlFetchDocs,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocuments(res.data);
			}

			if (res.pagination) {
				setPaginationDocuments(res.pagination);
			}
		},
	});

	// fetch acts
	const urlFetchActs = `${process.env.REACT_APP_API_URL}/acts?contracts=${contract.id}`;

	const { isLoading: isLoadingActs } = useFetch({
		isEnabled: Boolean(contract && isExpanded && tabValue === 'activities'),
		url: urlFetchActs,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setActs(res.data);
			}
		},
	});

	// fetch comments
	const urlFetchComments = `${process.env.REACT_APP_API_URL}/comments?contract=${contract.id}&populate=user`;

	const { isLoading: isLoadingComments, refetch: refetchComments } = useFetch({
		isEnabled: Boolean(contract && isExpanded && tabValue === 'comments'),
		url: urlFetchComments,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setComments(res.data);
			}
		},
	});

	// handlers
	const handleToggleCard = () => {
		setIsExpanded(!isExpanded);
	};
	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogContract');
		setSearchParams(searchParams);
	};

	const handleOpenDialog = (dialog: string) => {
		searchParams.set('dialog', dialog);
		searchParams.set('dialogContract', String(contract.id));
		setSearchParams(searchParams);
	};

	// vars
	const btnMenuItems: MenuItemProps[] = [];
	if (userProjectPermissions?.contract?.update) {
		btnMenuItems.push({
			children: t('editContract'),
			icon: <IconModeEdit />,
			onClick: () => {
				handleOpenDialog('editContract');
			},
		});
	}
	if (
		userProjectPermissions?.contract?.update &&
		contract.status === 'DRAFT' &&
		featureFlags['CONTRACT_WORKFLOW']
	) {
		btnMenuItems.push({
			children: t('submitForReview'),
			icon: <IconSend />,
			onClick: () => {
				handleOpenDialog('submitForReviewContract');
			},
		});
	}
	if (
		userProjectPermissions?.contract?.approve &&
		contract.status === 'SUBMITTED' &&
		featureFlags['CONTRACT_WORKFLOW']
	) {
		btnMenuItems.push({
			children: t('approveContract'),
			icon: <IconCheck />,
			onClick: () => {
				handleOpenDialog('approveContract');
			},
		});

		btnMenuItems.push({
			children: t('rejectContract'),
			icon: <IconClose />,
			onClick: () => {
				handleOpenDialog('rejectContract');
			},
		});
	}
	if (userProjectPermissions?.comment?.create) {
		btnMenuItems.push({
			children: t('addComment'),
			icon: <IconAddComment />,
			onClick: () => {
				handleOpenDialog('addComment');
			},
		});
	}
	if (userProjectPermissions?.document?.create) {
		btnMenuItems.push({
			children: t('uploadDocuments'),
			icon: <IconInsertDriveFile />,
			onClick: () => {
				handleOpenDialog('uploadDocsContract');
			},
		});
	}

	const tabs = [
		{
			label: t('transactions'),
			value: 'transactions',
		},
	];

	if (userProjectPermissions?.supplier?.read) {
		tabs.push({
			label: t('suppliers'),
			value: 'suppliers',
		});
	}

	if (userProjectPermissions?.document?.read) {
		tabs.push({
			label: t('documents'),
			value: 'documents',
		});
	}

	if (userProjectPermissions?.act?.read && featureFlags['ACTIVITIES']) {
		tabs.push({
			label: t('activities'),
			value: 'activities',
		});
	}

	if (userProjectPermissions?.comment?.read) {
		tabs.push({
			label: t('comments'),
			value: 'comments',
		});
	}

	tabs.push({
		label: t('description'),
		value: 'description',
	});

	let isProgressDeterminate = true;
	let progressPaidColor: LinearProgressProps['color'] = 'primary';
	let progressPaidValue = 0;
	let valueContractEntities: ReactNode = t('na');
	const contractSuppliers = (contract.entities as Entity[]).filter(
		(entity) => (entity as Entity).type === 'SUPPLIER'
	);

	if (contractSuppliers.length) {
		valueContractEntities = (
			<Grid container justifyContent={{ xs: 'flex-end', lg: 'flex-start' }} spacing={1}>
				<Grid item>
					<Grid container spacing={1}>
						<Grid item>
							<LinkEntity entity={contractSuppliers[0] as Entity} />
						</Grid>
						{contractSuppliers.length > 1 && <Grid item>+{contractSuppliers.length - 1}</Grid>}
					</Grid>
				</Grid>
			</Grid>
		);
	}

	// Calculate payment progress for multi-currency contracts
	if (contract.paid.length > 0 && contract.amounts.length > 0) {
		// Calculate percentage paid for each currency
		const currencyPercentages: number[] = [];

		contract.amounts.forEach((contractAmount) => {
			const paidAmount = contract.paid.find((paid) => paid.currency === contractAmount.currency);

			if (paidAmount && contractAmount.amount > 0) {
				const percentage = (paidAmount.amount / contractAmount.amount) * 100;
				currencyPercentages.push(Math.min(percentage, 100)); // Cap at 100%
			} else if (contractAmount.amount > 0) {
				currencyPercentages.push(0); // No payment for this currency
			}
		});

		// Calculate average percentage across all currencies
		if (currencyPercentages.length > 0) {
			progressPaidValue =
				currencyPercentages.reduce((sum, percent) => sum + percent, 0) / currencyPercentages.length;
			isProgressDeterminate = true;
			progressPaidColor = 'primary';
		} else {
			isProgressDeterminate = false;
			progressPaidColor = 'neutral';
		}
	}

	let status: DialogUpdateContractStatusPropsType['status'] | undefined;

	if (paramDialog === 'submitForReviewContract') {
		status = 'SUBMITTED';
	} else if (paramDialog === 'approveContract') {
		status = 'ACTIVE';
	} else if (paramDialog === 'rejectContract') {
		status = 'DRAFT';
	}

	const DialogUpdateContractStatusProps: Partial<DialogUpdateContractStatusPropsType> = {
		contract,
		isOpen: Boolean(status) && paramDialogContract === String(contract.id),
		onClose: handleCloseDialog,
		onSuccess: () => {
			if (refetchContracts) {
				refetchContracts();
			}
		},
	};

	if (status) {
		DialogUpdateContractStatusProps.status = status;
	}

	return (
		<>
			<Paper
				sx={(theme) => {
					const borderColor = isExpanded ? theme.palette.brand.blue[500] : 'transparent';
					return {
						border: `1px ${borderColor} solid`,
						position: 'relative',
					};
				}}>
				<Box
					sx={(theme) => {
						return {
							padding: theme.spacing(2),
						};
					}}>
					<Grid alignItems="center" container spacing={{ xs: 1, md: 2 }}>
						<Grid item xs={12} lg={frame === 'SUPPLIERS' ? 2 : 3}>
							<Box
								sx={(theme) => {
									return {
										margin: theme.spacing(0, 0, 2, 0),
										[theme.breakpoints.up('lg')]: {
											margin: 0,
										},
									};
								}}>
								<Grid alignItems="center" container spacing={2} wrap="nowrap">
									<Grid item>
										<IconCircle TooltipProps={{ title: t('contract') }} type="contract" />
									</Grid>
									<Grid item sx={{ minWidth: 0, flex: 1 }}>
										<TypographyCaption id={contract.id} />
										<Tooltip arrow={true} placement="top" title={contract.num}>
											<Typography
												fontWeight={600}
												sx={{
													display: 'inline-flex',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
												}}>
												{contract.num}
											</Typography>
										</Tooltip>
									</Grid>
								</Grid>
							</Box>
						</Grid>
						<Grid item xs={12} lg={1}>
							<RowKeyValue label={t('status')} value={<ChipStatus status={contract.status} />} />
						</Grid>
						{frame === 'SUPPLIERS' && (
							<Grid item xs={12} lg={1.5}>
								<RowKeyValue label={t('suppliers')} value={valueContractEntities} />
							</Grid>
						)}

						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('paid')}
								value={
									<Grid container spacing={1}>
										<Grid item xs={12}>
											<Grid
												container
												direction="column"
												alignItems={{ xs: 'flex-end', lg: 'flex-start' }}>
												{contract.paid.length ? (
													contract.paid.map((row, index) => {
														const amountRefunded = contract.refunded?.find(
															(refund) => refund.currency === row.currency
														)?.amount;
														const amountPaid = row.amount - (amountRefunded || 0);

														return (
															<Grid item key={index}>
																<Amount currency={row.currency} value={amountPaid} />
															</Grid>
														);
													})
												) : (
													<Typography>{t('na')}</Typography>
												)}
											</Grid>
										</Grid>
										<Grid
											item
											sx={{
												display: 'flex',
												justifyContent: {
													xs: 'flex-end',
													lg: 'flex-start',
												},
											}}
											xs={12}>
											<Tooltip
												arrow={true}
												placement="bottom"
												title={
													isProgressDeterminate
														? Intl.NumberFormat('en-US', { style: 'percent' }).format(
																progressPaidValue / 100
															)
														: t('na')
												}>
												<LinearProgress
													color={progressPaidColor}
													sx={{
														width: {
															xs: 80,
															md: 120,
															lg: '100%',
														},
													}}
													value={progressPaidValue}
													variant="determinate"
												/>
											</Tooltip>
										</Grid>
									</Grid>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('amounts')}
								value={
									contract.amounts.length > 0 ? (
										<Grid container spacing={0}>
											{contract.amounts.map((amount, index) => (
												<Grid item xs={12} key={amount.id || index}>
													<Amount
														currency={amount.currency}
														TypographyProps={{ fontWeight: 500 }}
														value={amount.amount}
													/>
												</Grid>
											))}
										</Grid>
									) : (
										<Typography>{t('na')}</Typography>
									)
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1}>
							<RowKeyValue
								label={t('stepId')}
								value={<Typography>{contract.idStep || t('na')}</Typography>}
							/>
						</Grid>
						<Grid item xs={12} lg={1}>
							<RowKeyValue
								label={t('signingDate')}
								value={
									contract?.dateSigning ? (
										<Time date={new Date(contract?.dateSigning)} />
									) : (
										<Typography>{t('na')}</Typography>
									)
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('updated')}
								value={
									<UserAvatarModified
										contract={contract}
										activityTypes={[
											'CONTRACT_UPDATED',
											'CONTRACT_SUBMITTED',
											'CONTRACT_APPROVED',
											'CONTRACT_RETURNED',
											'CONTRACT_CREATED',
										]}
									/>
								}
							/>
						</Grid>

						<Grid item xs={12} lg={true}>
							<Grid alignItems="center" container justifyContent={{ lg: 'flex-end' }} spacing={1}>
								{btnMenuItems.length > 0 && (
									<Grid
										item
										sx={{
											position: 'absolute',
											top: 0,
											right: theme.spacing(1),
											[theme.breakpoints.up('lg')]: {
												position: 'relative',
												top: 'auto',
												right: 'auto',
											},
										}}>
										<BtnMenu items={btnMenuItems} />
									</Grid>
								)}
								<Grid item>
									<BtnExpand
										isExpanded={isExpanded}
										label={isExpanded ? t('hideDetails') : t('seeDetails')}
										onClick={handleToggleCard}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Box>
				{isExpanded && (
					<TabContext value={tabValue}>
						<Tabs
							BoxProps={{
								sx: {
									marginTop: {
										lg: theme.spacing(3),
									},
								},
							}}
							hasBorder={false}
							onChange={handleChangeTab}
							tabs={tabs}
							value={tabValue}
						/>
						<TabPanel value="transactions">
							<TableBasicTransactions
								data={transactions}
								isLoading={isLoadingTransactions}
								headersIncluded={['amount', 'date', 'recorded', 'tax', 'total', 'type', 'updated']}
							/>
						</TabPanel>
						<TabPanel value="suppliers">
							<TableBasicEntities data={entities} isLoading={isLoadingEntities} />
						</TabPanel>
						<TabPanel value="documents">
							<TableBasicDocs
								data={documents}
								isLoading={isLoadingDocs}
								pagination={paginationDocuments}
								refetchDocuments={refetchDocuments}
							/>
						</TabPanel>
						<TabPanel value="activities">
							<TableBasicActs data={acts} isLoading={isLoadingActs} />
						</TabPanel>
						<TabPanel value="comments">
							<TableBasicComments data={comments} isLoading={isLoadingComments} />
						</TabPanel>
						<TabPanel value="description">
							<BoxDescription description={contract?.description} />
						</TabPanel>
					</TabContext>
				)}
			</Paper>
			{hasDialogEditContract && (
				<DialogEditContract
					contract={contract}
					isOpen={paramDialog === 'editContract' && paramDialogContract === String(contract.id)}
					onClose={() => {
						searchParams.delete('dialog');
						searchParams.delete('dialogContract');
						setSearchParams(searchParams);
					}}
					onSuccess={() => {
						if (refetchContracts) {
							refetchContracts();
						}
					}}
				/>
			)}
			<DialogAddComment
				isOpen={paramDialog === 'addComment' && paramDialogContract === String(contract.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchComments) {
						refetchComments();
					}
				}}
			/>
			<DialogUploadDocs
				DialogActionsProps={{ subtitle: { children: contract.num } }}
				isOpen={paramDialog === 'uploadDocsContract' && paramDialogContract === String(contract.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchContracts) {
						refetchContracts();
					}
				}}
			/>
			{DialogUpdateContractStatusProps.status && (
				<DialogUpdateContractStatus
					{...(DialogUpdateContractStatusProps as DialogUpdateContractStatusPropsType)}
				/>
			)}
		</>
	);
};
